/* global Vue, PROPERTIES */

import { startLoading, stopLoading, setErrorAndStopLoading } from './Notifications'

export default {
  name: 'OfferFilters',
  template: `
<div class="booking-menu no-margin bg-beige-50">
  <div class="container grid select-filters">
    <span class="col-12 large-text uppercase" v-show="isForVariations">{{ $t('offers.filter') }}</span>

    <div class="form-element col-12 col-xs-6 col-sm-4 col-lg-3 col-xl-2" v-show="!isForVariations && holidayTypes.length > 1">
      <label for="holidayTypeFilter">{{ $t('holidayTypeFilter') }}</label>
      <select class="select-input" v-model="offerParams.holidayType" id="holidayTypeFilter" @change="updateOptions">
        <option value="">{{ $t('offers.allHolidays') }}</option>
        <option v-for="holidayType in holidayTypes" :text="holidayType.title" :value="holidayType.slug" />
      </select>
    </div>

    <div class="form-element col-12 col-xs-6 col-sm-4 col-lg-3 col-xl-2">
      <label for="brandFilter">{{ $t('interestsTab.brand') }}</label>
      <select class="select-input" v-model="offerParams.brand" id="brandFilter" @change="updateOptions">
        <option value="">{{ $t('allBrands') }}</option>
        <option v-for="brand in brands" :text="capitalize(brand)" :value="brand" />
      </select>
    </div>

    <div class="form-element col-12 col-xs-6 col-sm-4 col-lg-3 col-xl-2">
      <label for="destinationFilter">{{ $t('destination') }}</label>
      <select class="select-input" v-model="offerParams.destination" id="destinationFilter" @change="updateOptions">
        <option value="">{{ $t('allDestinations') }}</option>
        <option v-for="destination in destinations" :text="capitalize(destination)" :value="destination" />
      </select>
    </div>

    <div class="form-element col-12 col-xs-6 col-sm-4 col-lg-3 col-xl-2">
      <label for="propertyFilter">{{ $t('property') }}</label>
      <select class="select-input" v-model="offerParams.propertyId" id="propertyFilter" @change="updateOptions">
        <option value="">{{ $t('offers.allProperties') }}</option>
        <option v-for="property in filteredPropertyOptions" :text="property.title" :value="property.value" />
      </select>
    </div>

    <div class="form-element booking-menu__update col-12 col-xs-6 col-sm-4 col-lg-3 col-xl-2 offset-xs-6 offset-sm-8 offset-lg-9 offset-xl-10 text-right" v-show="!isForVariations">
      <button class="button" @click="updateResults">{{ $t('bookingModal.updateBtn') }}</button>
    </div>
  </div>
</div>
<div class="section-margin-small-bottom"></div>
  `,
  props: {
    isForVariations: {
      type: Boolean,
      default: false,
    },
    properties: {
      type: Array,
    },
    availableProperties: {
      type: Array,
    },
    offers: {
      type: Array,
    },
  },
  emits: ['offerParamsChanged', 'error'],
  setup (props, { emit }) {
    const offerParams = Vue.reactive({
      propertyId: '',
      holidayType: '',
      brand: '',
      destination: '',
    })

    const propertyOptions = Vue.ref([])
    const holidayTypes = Vue.ref([])
    const allHolidayTypes = Vue.ref([])
    const { offers, availableProperties } = props

    const filteredProperties = Vue.computed(() => {
      if (
        !offerParams.propertyId &&
        !offerParams.brand &&
        !offerParams.destination) {
        return props.properties
      }

      return props.properties.filter(p => {
        const matchesProperty = (
          !offerParams.propertyId ||
          offerParams.propertyId === p.title ||
          offerParams.propertyId === p.groupTitle ||
          offerParams.propertyId === p.bundleTitle
        )
        const matchesBrand = !offerParams.brand || p.brand === offerParams.brand
        const matchesDestination = !offerParams.destination || p.destination === offerParams.destination

        return matchesProperty && matchesBrand && matchesDestination
      })
    })

    const brands = Vue.computed(() => {
      const distinct = new Set()
      return filteredProperties.value
        .filter(property => !availableProperties || availableProperties.includes(property.propertyGroup))
        .map(property => property.brand || '').filter((item) => {
          return !item || distinct.has(item) ? false : (distinct.add(item))
        })
    })

    const destinations = Vue.computed(() => {
      const distinct = new Set()
      return filteredProperties.value
        .filter(property => !availableProperties || availableProperties.includes(property.propertyGroup))
        .filter(property => property.brand === offerParams.brand || !offerParams.brand)
        .map(property => property.destination || '')
        .filter(item => !item || distinct.has(item) ? false : (distinct.add(item)))
    })

    const filteredPropertyOptions = Vue.computed(() => {
      if (!offerParams.propertyId && !offerParams.brand && !offerParams.destination) {
        return propertyOptions.value
      }
      return propertyOptions.value.filter(propertyGroup => {
        const hasBrand = propertyGroup.brand === offerParams.brand || !offerParams.brand
        const hasDestination = propertyGroup.destination === offerParams.destination || !offerParams.destination
        return hasBrand && hasDestination
      })
    })

    const filteredHolidayTypes = Vue.computed(() => {
      if (offerParams.propertyId === '') {
        return allHolidayTypes.value
      }

      const filteredOfferList = offers.filter(offer => offer.properties.includes(offerParams.propertyId))
      const availableProperties = filteredOfferList.reduce((properties, offer) => { properties.push(...offer.properties); return properties }, [])

      return allHolidayTypes.value.filter(holiday => holiday.properties.find(property => availableProperties.includes(property)))
    })

    function updateResults (updateUrl = true) {
      if (updateUrl) updateURL()
      if (!props.isForVariations) holidayTypes.value = filteredHolidayTypes.value

      emit('offerParamsChanged', {
        holidayType: offerParams.holidayType,
        properties: filteredProperties.value.map(property => `${property.propertyGroup}${property.campingType ? '-' + property.campingType : ''}`),
      })
    }

    function updateOptions (event) {
      switch (event.target.id) {
        case 'destinationFilter':
          offerParams.propertyId = ''
          break
        case 'brandFilter':
          offerParams.propertyId = ''
          offerParams.destination = ''
          break
        case 'holidayTypeFilter':
          offerParams.propertyId = ''
          offerParams.destination = ''
          offerParams.brand = ''
          break
        default:
          break
      }

      if (props.isForVariations) updateResults()
    }

    function updateURL () {
      const urlParams = `propertyId=${encodeURIComponent(offerParams.propertyId)}` +
        (props.isForVariations ? '' : `&holidayType=${encodeURIComponent(offerParams.holidayType)}`) +
        `&brand=${encodeURIComponent(offerParams.brand)}` +
        `&destination=${encodeURIComponent(offerParams.destination)}`
      window.history.pushState(
        'page',
        'OfferFilter',
        `${window.location.pathname}?${urlParams}`)
    }

    function capitalize (string) {
      return string.charAt(0).toUpperCase() + string.slice(1).replace('-', ' ')
    }

    function setPropertyOptions () {
      propertyOptions.value = []
      PROPERTIES.forEach((propertyGroup) => {
        const isGroup = propertyGroup.isPropertyGroup
        const bundle = propertyGroup.bundle

        const subProperties = isGroup ? props.properties.filter((p) => p.propertyGroup === propertyGroup.slug) : []

        if (!props.availableProperties || props.availableProperties.includes(propertyGroup.slug)) {
          const option = {
            ...propertyGroup,
            value: propertyGroup.title,
          }
          if (bundle) {
            option.title = `\xa0\xa0${option.title}`
          }
          propertyOptions.value.push(option)
        }

        propertyOptions.value.push(
          ...subProperties
            .filter(sp => !props.availableProperties ||
              props.availableProperties.includes(`${sp.propertyGroup}-${sp.campingType}`))
            .map(({ title }) => ({
              ...propertyGroup,
              title: bundle ? `\xa0\xa0\xa0\xa0${title}` : `\xa0\xa0${title}`,
              value: title,
            })),
        )
      })
    }

    async function getHolidayTypes () {
      if (props.isForVariations) return

      const response = await fetch(window.HOLIDAY_LIST_URL)
      if (!response.ok) {
        throw new Error(response.statusText)
      }

      allHolidayTypes.value = await response.json()
      holidayTypes.value = allHolidayTypes.value
    }

    Vue.onMounted(async () => {
      const urlParams = new URLSearchParams(window.location.search);
      ['propertyId', 'holidayType', 'brand', 'destination'].forEach(slug => {
        offerParams[slug] = decodeURIComponent(urlParams.get(slug) || '') || offerParams[slug]
      })

      try {
        startLoading('offers-filter-mount')
        await getHolidayTypes()
        setPropertyOptions()
        if (filteredProperties.value) {
          updateResults(false)
        }
        stopLoading('offers-filter-mount')
      } catch (err) {
        setErrorAndStopLoading(err)
      }
    })

    return {
      filteredPropertyOptions,
      holidayTypes,
      brands,
      destinations,
      offerParams,
      updateResults,
      capitalize,
      updateOptions,
    }
  },
}
